import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Divider, Skeleton, Stack, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { hasAdminAccess } from 'common/src/authorization/accessChecks';

import {
  createAlertNote,
  deleteAlertNote,
  getAlertNotes,
  updateAlertNote,
} from 'portal/src/api/AlertsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import useAuth from 'portal/src/auth/useAuth';
import ConfirmDeleteDialog from 'portal/src/components/common/tables/ConfirmDeleteDialog';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import AlertNoteItem from './AlertNoteItem';

const UNCOLLAPSED_NOTE_COUNT = 2;

const AlertNotesSection = ({ alertId }: { alertId: string }) => {
  const queryClient = useQueryClient();
  const snackbarHandler = useSnackbarHandler();
  const { getUser } = useAuth();
  const user = getUser();

  const [isExpanded, setIsExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteIdToDelete, setNoteIdToDelete] = useState<number | null>(null);

  const { data: notesData, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.alertNotes, alertId, isExpanded],
    queryFn: () =>
      getAlertNotes(alertId, isExpanded ? undefined : UNCOLLAPSED_NOTE_COUNT),
  });

  const notes = notesData?.data ?? [];
  const totalCount = notesData?.meta?.totalCount ?? 0;
  const hiddenCount = totalCount - UNCOLLAPSED_NOTE_COUNT;

  const invalidateNotes = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.alertNotes, alertId],
    });
  };

  const { mutateAsync: addNote, isPending: isAdding } = useMutation({
    mutationFn: () => createAlertNote(alertId, draft.trim()),
    onSuccess: async () => {
      setDraft('');
      await invalidateNotes();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  const { mutateAsync: editNote, isPending: isEditing } = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      updateAlertNote(alertId, id, note),
    onSuccess: async () => {
      await invalidateNotes();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  const { mutate: removeNote, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteAlertNote(alertId, noteIdToDelete),
    onSuccess: async () => {
      setNoteIdToDelete(null);
      await invalidateNotes();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  return (
    <>
      <Divider sx={{ my: 1 }}>
        Notes{totalCount > 0 ? ` (${totalCount})` : ''}
      </Divider>
      <Stack spacing={1}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Add a note about this alert..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={isEditingNote}
          slotProps={{ input: { sx: { fontSize: 14 } } }}
        />
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          {draft && (
            <Button
              color="info"
              size="small"
              onClick={() => setDraft('')}
              disabled={isEditingNote}
            >
              Cancel
            </Button>
          )}
          <Button
            color="info"
            size="small"
            variant="contained"
            onClick={() => void addNote()}
            disabled={isAdding || isEditingNote || !draft.trim()}
          >
            Save
          </Button>
        </Stack>

        {isLoading ? (
          <Skeleton variant="rounded" height={64} />
        ) : (
          notes.map((note) => (
            <AlertNoteItem
              key={note.id}
              canModify={
                note.createdBy === user?.id || hasAdminAccess(user?.userType)
              }
              isEditingDisabled={isEditingNote}
              isSaving={isEditing}
              note={note}
              onDelete={setNoteIdToDelete}
              onEditingChange={setIsEditingNote}
              onUpdate={(id, updated) => editNote({ id, note: updated })}
            />
          ))
        )}

        {hiddenCount > 0 && (
          <Button
            size="small"
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            sx={{ textTransform: 'none' }}
            color="info"
          >
            {isExpanded
              ? 'Show fewer notes'
              : `Show ${hiddenCount} more note${hiddenCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </Stack>
      <ConfirmDeleteDialog
        isLoading={isDeleting}
        itemName="note"
        showDialog={noteIdToDelete !== null}
        onClose={() => setNoteIdToDelete(null)}
        onConfirm={removeNote}
      />
    </>
  );
};

export default AlertNotesSection;
